/**
 * Dependencies Service - Node.js ecosystem
 */
import { promises as fs } from 'fs';
import path from 'path';
import { execSync } from 'child_process';
import pLimit from 'p-limit';
import type { 
    DepsServiceInterface, 
    PackageInfo, 
    CheckResult, 
    UpgradeOptions,
    PackageManager,
    UpgradeType
} from '../types/deps.types.js';

export interface PackageJson {
    name?: string;
    version?: string;
    dependencies?: Record<string, string>;
    devDependencies?: Record<string, string>;
}

export class DepsService implements DepsServiceInterface {
    readonly ecosystem = 'node' as const;
    private versionCache: Map<string, string> = new Map();

    async check(cwd: string, onProgress?: (completed: number, total: number) => void): Promise<CheckResult> {
        const pkgJson = await this.readPackageJson(cwd);
        const manager = await this.detectPackageManager(cwd);
        const packages: Array<{ name: string; current: string; type: 'dep' | 'dev' }> = [];

        if (pkgJson.dependencies) {
            for (const [name, version] of Object.entries(pkgJson.dependencies)) {
                if (this.isValidNpmDependency(version)) {
                    packages.push({ name, current: version, type: 'dep' });
                }
            }
        }

        if (pkgJson.devDependencies) {
            for (const [name, version] of Object.entries(pkgJson.devDependencies)) {
                if (this.isValidNpmDependency(version)) {
                    packages.push({ name, current: version, type: 'dev' });
                }
            }
        }

        const upgradablePackages = await this.fetchBulkVersions(packages, onProgress);
        return { ecosystem: this.ecosystem, manager, packages: upgradablePackages };
    }

    async upgrade(cwd: string, options: UpgradeOptions): Promise<void> {
        const manager = await this.detectPackageManager(cwd);
        const commands = this.buildUpgradeCommands(options.packages, manager);
        if (commands.deps) this.executeUpgrade(commands.deps);
        if (commands.devDeps) this.executeUpgrade(commands.devDeps);
    }

    async readPackageJson(cwd: string): Promise<PackageJson> {
        const pkgPath = path.join(cwd, 'package.json');
        try {
            const content = await fs.readFile(pkgPath, 'utf-8');
            return JSON.parse(content) as PackageJson;
        } catch (error) {
            if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
                throw new Error('Không tìm thấy package.json trong thư mục hiện tại');
            }
            throw new Error(`Lỗi đọc package.json: ${(error as Error).message}`);
        }
    }

    async fetchLatestVersion(packageName: string): Promise<string | null> {
        if (this.versionCache.has(packageName)) {
            return this.versionCache.get(packageName)!;
        }

        try {
            const response = await fetch(
                `https://registry.npmjs.org/${encodeURIComponent(packageName)}/latest`,
                { signal: AbortSignal.timeout(10000) }
            );

            if (!response.ok) return null;
            const data = await response.json() as { version: string };
            this.versionCache.set(packageName, data.version);
            return data.version;
        } catch {
            return null;
        }
    }

    async fetchBulkVersions(
        packages: Array<{ name: string; current: string; type: 'dep' | 'dev' }>,
        onProgress?: (completed: number, total: number) => void
    ): Promise<PackageInfo[]> {
        const limit = pLimit(10);
        const total = packages.length;
        let completed = 0;

        const results = await Promise.all(
            packages.map(pkg => limit(async () => {
                const latest = await this.fetchLatestVersion(pkg.name);
                completed++;
                onProgress?.(completed, total);

                if (!latest) return null;
                const currentClean = this.stripSemverPrefix(pkg.current);
                const canUpgrade = this.isNewerVersion(latest, currentClean);
                const upgradeType = this.getUpgradeType(currentClean, latest);

                return {
                    name: pkg.name,
                    current: pkg.current,
                    latest,
                    latestWithPrefix: this.preserveSemverPrefix(pkg.current, latest),
                    type: pkg.type,
                    canUpgrade,
                    upgradeType
                } satisfies PackageInfo;
            }))
        );

        return results.filter((r): r is PackageInfo => r !== null && r.canUpgrade);
    }

    private isValidNpmDependency(version: string): boolean {
        const invalidPrefixes = ['git+', 'git:', 'file:', 'link:', 'workspace:'];
        return !invalidPrefixes.some(prefix => version.startsWith(prefix));
    }

    private getUpgradeType(current: string, latest: string): UpgradeType {
        const currentParts = current.split('.').map(Number);
        const latestParts = latest.split('.').map(Number);
        if ((latestParts[0] || 0) > (currentParts[0] || 0)) return 'major';
        if ((latestParts[1] || 0) > (currentParts[1] || 0)) return 'minor';
        return 'patch';
    }

    stripSemverPrefix(version: string): string {
        return version.replace(/^[~^>=<]+/, '');
    }

    preserveSemverPrefix(current: string, latest: string): string {
        const match = current.match(/^([~^>=<]*)/);
        const prefix = match?.[1] || '';
        return prefix + latest;
    }

    isNewerVersion(remote: string, local: string): boolean {
        const remoteParts = remote.split('.').map(Number);
        const localParts = local.split('.').map(Number);

        for (let i = 0; i < 3; i++) {
            const r = remoteParts[i] || 0;
            const l = localParts[i] || 0;
            if (r > l) return true;
            if (r < l) return false;
        }
        return false;
    }

    async detectPackageManager(cwd: string): Promise<PackageManager> {
        const lockFiles: Array<{ file: string; pm: PackageManager }> = [
            { file: 'pnpm-lock.yaml', pm: 'pnpm' },
            { file: 'yarn.lock', pm: 'yarn' },
            { file: 'bun.lockb', pm: 'bun' },
            { file: 'package-lock.json', pm: 'npm' },
        ];

        for (const { file, pm } of lockFiles) {
            try {
                await fs.access(path.join(cwd, file));
                return pm;
            } catch {}
        }

        const userAgent = process.env.npm_config_user_agent || '';
        if (userAgent.includes('pnpm')) return 'pnpm';
        if (userAgent.includes('yarn')) return 'yarn';
        if (userAgent.includes('bun')) return 'bun';
        if (this.isCommandAvailable('pnpm')) return 'pnpm';
        return 'npm';
    }

    private isCommandAvailable(command: string): boolean {
        try {
            execSync(`${command} --version`, { stdio: ['pipe', 'pipe', 'pipe'] });
            return true;
        } catch {
            return false;
        }
    }

    getUpgradeCommands(packages: PackageInfo[]): { deps: string | null; devDeps: string | null } {
        const pm = process.env.npm_config_user_agent?.includes('pnpm') ? 'pnpm' : 'npm';
        return this.buildUpgradeCommands(packages, pm as PackageManager);
    }

    private buildUpgradeCommands(packages: PackageInfo[], pm: PackageManager): { deps: string | null; devDeps: string | null } {
        const deps = packages.filter(p => p.type === 'dep');
        const devDeps = packages.filter(p => p.type === 'dev');

        const formatPackages = (pkgs: PackageInfo[]) => pkgs.map(p => `${p.name}@${p.latestWithPrefix}`).join(' ');

        let depsCmd: string | null = null;
        let devDepsCmd: string | null = null;

        if (deps.length > 0) {
            const pkgList = formatPackages(deps);
            switch (pm) {
                case 'pnpm': depsCmd = `pnpm add ${pkgList}`; break;
                case 'yarn': depsCmd = `yarn add ${pkgList}`; break;
                case 'bun': depsCmd = `bun add ${pkgList}`; break;
                default: depsCmd = `npm install ${pkgList}`;
            }
        }

        if (devDeps.length > 0) {
            const pkgList = formatPackages(devDeps);
            switch (pm) {
                case 'pnpm': devDepsCmd = `pnpm add -D ${pkgList}`; break;
                case 'yarn': devDepsCmd = `yarn add -D ${pkgList}`; break;
                case 'bun': devDepsCmd = `bun add -D ${pkgList}`; break;
                default: devDepsCmd = `npm install -D ${pkgList}`;
            }
        }

        return { deps: depsCmd, devDeps: devDepsCmd };
    }

    executeUpgrade(command: string): void {
        execSync(command, { stdio: 'inherit', env: { ...process.env, FORCE_COLOR: '1' } });
    }
}
